import { PlusOutlined } from "@ant-design/icons"
import {
  Button,
  Drawer,
  Empty,
  Flex,
  Input,
  List,
  Tooltip,
  Typography
} from "antd"
import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import type {
  CatalogWidgetSpec,
  WidgetCatalog
} from "../../../DashboardPage/redux/widgets.types.ts"
import { groupCatalog } from "../../helpers/reportCatalog.ts"

const { Text } = Typography

interface ReportWidgetLibraryDrawerProps {
  open: boolean
  catalog: WidgetCatalog | null
  loading: boolean
  onAdd: (spec: CatalogWidgetSpec) => void
  onClose: () => void
  addedSpecKeys?: ReadonlySet<string>
}

const EMPTY_SPEC_KEYS: ReadonlySet<string> = new Set()

export const ReportWidgetLibraryDrawer = ({
  open,
  catalog,
  loading,
  onAdd,
  onClose,
  addedSpecKeys = EMPTY_SPEC_KEYS
}: ReportWidgetLibraryDrawerProps) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState("")

  const groups = useMemo(() => groupCatalog(catalog, search), [catalog, search])

  return (
    <Drawer
      title={t("reports.library.title")}
      open={open}
      onClose={onClose}
      width={420}
      destroyOnHidden
    >
      <Flex vertical gap="middle">
        <Input.Search
          placeholder={t("reports.library.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
        />

        {!loading && groups.length === 0 && (
          <Empty description={t("reports.library.empty")} />
        )}

        {groups.map((group) => (
          <Flex vertical gap="small" key={group.key}>
            <Text strong>{t(`reports.library.groups.${group.key}`)}</Text>
            <List
              size="small"
              loading={loading}
              dataSource={group.specs}
              renderItem={(spec) => {
                const added = addedSpecKeys.has(spec.key)
                return (
                  <List.Item
                    key={spec.key}
                    actions={[
                      <Tooltip
                        key="add"
                        title={added ? t("reports.library.alreadyAdded") : ""}
                      >
                        <Button
                          type="text"
                          size="small"
                          icon={<PlusOutlined />}
                          aria-label={spec.title}
                          disabled={added}
                          onClick={() => onAdd(spec)}
                        />
                      </Tooltip>
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        added ? (
                          <Text type="secondary">{spec.title}</Text>
                        ) : (
                          spec.title
                        )
                      }
                    />
                  </List.Item>
                )
              }}
            />
          </Flex>
        ))}
      </Flex>
    </Drawer>
  )
}
